/**
 * App shell + boot path (ARCHITECTURE.md §5, P1.md + P2.md):
 *
 *  1. open IndexedDB and PAINT from it — before ANY network (<2s budget);
 *  2. only then start the sync machinery: outbox init (+ leftover flush), one
 *     catch-up delta pull, the SSE tickle stream, and the watchdog;
 *  3. offline/evicted → honest-empty state, never a blank screen (the error
 *     boundary in main.tsx is the last backstop).
 *
 * SHELL = the COCKPIT (Claude Design bundle, docs/design/cockpit/): ONE
 * responsive tree — black signage bar with the [g]igs/[f]ichiers/[r]olodex/
 * [réglages]/[aide] nav, the tour-book rail (RUN OF SHOW · À TRAITER ·
 * JOURNAL · ARCHIVE), the open-gig briefing, and the docked always-on
 * console at the bottom (keyboard-first desktop, vocal-first mobile). All
 * mutations remain Ops through the ONE Editor (CONSTRAINTS #1).
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { detectLocale, loadCatalog, persistLocale, t, type Locale } from "../i18n.ts";
import { MemoryStore, openLocalDb, type GigRecord, type LocalStore, type SyncMeta } from "../db/idb.ts";
import type { OutboxStore } from "../outbox/outbox.ts";
import type { Flusher, FlushStatus } from "../outbox/flush.ts";
import type { Editor } from "../outbox/mutations.ts";
import type { UndoStack } from "../undo/undo.ts";
import type { ConflictTracker } from "../sync/conflicts.ts";
import type { SyncEngine } from "../sync/engine.ts";
import type { SseHandle } from "../sync/sse.ts";
import type { SyncMode } from "../sync/watchdog.ts";
import { ensurePersistentStorage, isStandalone } from "./installGate.ts";
import { requestSwUpdate } from "./swRegistration.ts";
import { CapabilitiesProvider, detectCapabilities } from "./capabilities.tsx";
import type { UploadQueue } from "../files/uploadQueue.ts";
import type { LoginSurfaces } from "../views/Login.tsx";
import { registry } from "@td/registry";
import { computeTravelWindow } from "../boot/travelWindow.ts";
import { compareGigs, gigDisplayName } from "../views/GigList.tsx";
import type { AttentionItem } from "../views/attentionApi.ts";
import { computeVerdict, pickTodayFocus } from "../views/Today.tsx";
import { GlanceHero } from "../views/GlanceHero.tsx";
import { postGlance } from "../native/bridge.ts";
import { toTDGlance } from "../glance/resolveGlance.ts";
import type { SplitId } from "../views/splits.ts";
import { SignageBar } from "../cockpit/SignageBar.tsx";
import { Rail, type JournalEntry, type MissionEntry } from "../cockpit/Rail.tsx";
import { Console, type CockpitView } from "../cockpit/Console.tsx";
import type { ApplyCard } from "../omnibar/brain.ts";
import "../cockpit/cockpit.css";
// Lazy chunks (weight law + the lazy-consts-AFTER-imports rule, STATE.md):
const Login = lazy(() => import("../views/Login.tsx").then((m) => ({ default: m.Login })));
const Attention = lazy(() => import("../views/Attention.tsx").then((m) => ({ default: m.Attention })));
const FilesVault = lazy(() => import("../files/FilesVault.tsx").then((m) => ({ default: m.FilesVault })));
const Rolodex = lazy(() => import("../people/Rolodex.tsx").then((m) => ({ default: m.Rolodex })));
const Settings = lazy(() => import("../cockpit/Settings.tsx").then((m) => ({ default: m.Settings })));
const NewGigFiche = lazy(() => import("../cockpit/NewGigFiche.tsx").then((m) => ({ default: m.NewGigFiche })));
// Weight law: the briefing (GigDetail + DayRun + editors + files list) is the
// heaviest surface — lazy. The ADR-011 fast lane is the TravelCard in
// main.tsx, which stays in the shell; the rail paints instantly from IDB.
const GigDetail = lazy(() => import("../views/GigDetail.tsx").then((m) => ({ default: m.GigDetail })));

const WATCHDOG_TICK_MS = 5_000;
/** Viewers have no SSE (that's a session-authed stream); they re-poll their
 * scoped gigs on this cadence and on every connectivity window. */
const VIEWER_POLL_MS = 60_000;
const TUTORIAL_STEPS = 6;

interface BootState {
  painted: boolean; // local paint happened (with or without data)
  gigs: GigRecord[]; // active (not trashed, not purged)
  all: GigRecord[]; // every gig incl. tombstoned — the Rail derives the Trash from this
  meta: SyncMeta | null;
}

/** Replication probe for the save state's synced-vs-replicated distinction. */
async function checkReplication(): Promise<boolean> {
  // /health, NOT /healthz — Cloud Run's frontend intercepts the literal
  // /healthz and never reaches Fastify (empirically proven P1).
  const res = await fetch("/health", { headers: { Accept: "application/json" } });
  if (!res.ok) return false;
  const body = (await res.json()) as { litestream?: { replicated?: boolean } };
  return body.litestream?.replicated === true;
}

type Theme = "auto" | "night" | "day";

function readTheme(): Theme {
  try {
    const v = localStorage.getItem("td-theme");
    return v === "night" || v === "day" ? v : "auto";
  } catch {
    return "auto";
  }
}

function applyTheme(theme: Theme): void {
  if (theme === "auto") delete document.documentElement.dataset["theme"];
  else document.documentElement.dataset["theme"] = theme;
}

export function App() {
  const [locale, setLocale] = useState<Locale>(detectLocale);
  const [boot, setBoot] = useState<BootState>({ painted: false, gigs: [], all: [], meta: null });
  const [view, setView] = useState<CockpitView | "attention">("gig");
  const [selected, setSelected] = useState<{ gigId: string; split: SplitId } | null>(null);
  const capabilities = useMemo(detectCapabilities, []);
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [, setMode] = useState<SyncMode | "connecting">("connecting");
  /** Non-null = confirmed 401 by /api/me: render Login. Offline NEVER sets this. */
  const [loginSurfaces, setLoginSurfaces] = useState<LoginSurfaces | null>(null);

  // P2 save-state inputs.
  const [pendingCount, setPendingCount] = useState(0);
  const [flushStatus, setFlushStatus] = useState<FlushStatus>({ flushing: false, replicated: false, held: false });
  const [attentionItems, setAttentionItems] = useState<readonly AttentionItem[]>([]);
  const [engineAttention, setEngineAttention] = useState(0);
  const [, setConflictTick] = useState(0); // repaint when chips change

  // Cockpit state: journal, tutorial, prefs, new-gig fiche.
  const [journal, setJournal] = useState<readonly JournalEntry[]>([]);
  const [tourStep, setTourStep] = useState(-1);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [units, setUnits] = useState<"km" | "mi">(() => {
    try {
      return localStorage.getItem("td-units") === "mi" ? "mi" : "km";
    } catch {
      return "km";
    }
  });
  const [newGig, setNewGig] = useState<{ city?: string; day?: string } | null | false>(false);
  const [installDismissed, setInstallDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem("td-install-dismissed") === "1"; } catch { return true; }
  });
  /** Console file-hit → the vault opens focused on this files item. */
  const [vaultFocusFile, setVaultFocusFile] = useState<string | null>(null);
  const prefillRef = useRef<((q: string) => void) | null>(null);
  const externalCardsRef = useRef<((cards: readonly ApplyCard[], context: string) => void) | null>(null);
  /** THE FLEET: agent missions in flight/finished — several run in parallel. */
  const [missions, setMissions] = useState<readonly MissionEntry[]>([]);
  /** Post-paint machinery module (app/machinery.ts) — loaded in run(). */
  const machineryRef = useRef<typeof import("./machinery.ts") | null>(null);

  useEffect(() => applyTheme(theme), [theme]);

  const standalone = useMemo(isStandalone, []);
  /** ONE responsive tree (bundle §5bis): <760px = mobile column, no fork. */
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 760px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 760px)");
    const on = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const storeRef = useRef<(LocalStore & OutboxStore) | null>(null);
  const engineRef = useRef<SyncEngine | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const flusherRef = useRef<Flusher | null>(null);
  const undoRef = useRef<UndoStack | null>(null);
  const conflictsRef = useRef<ConflictTracker | null>(null);
  const viewerReloadRef = useRef<(() => void) | null>(null); // set in viewer sessions only
  const pullRef = useRef<(() => void) | null>(null); // sign-in resumes sync through this
  const uploadQueueRef = useRef<UploadQueue | null>(null);
  const [uploadQueueReady, setUploadQueueReady] = useState(false);

  const pushJournal = useCallback((text: string, tone: JournalEntry["tone"]) => {
    const time = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    setJournal((j) => [{ time, text, tone }, ...j].slice(0, 6));
  }, []);

  const tutorialTick = useCallback(
    (evt: "cards" | "apply" | "undo" | "fiche" | "view") => {
      setTourStep((step) => {
        const expected = ["cards", "apply", "undo", "fiche", "view", null][step];
        if (step < 0 || expected !== evt) return step;
        return step + 1 >= TUTORIAL_STEPS ? -1 : step + 1;
      });
    },
    [],
  );

  const repaint = useCallback(async () => {
    const store = storeRef.current;
    if (store === null) return;
    const [allGigs, meta] = await Promise.all([store.listGigs(), store.getMeta()]);
    // Delete is a MOVE, not an erase (Eduardo 2026-07-04: "delete never deletes,
    // just move to trash"). A `deleted` gig leaves the active surfaces but lives
    // on in the Trash, recoverable. A `purged` gig (permanent delete) is gone
    // from EVERY surface incl. the Trash — but still retained server-side
    // (CONSTRAINTS #2: no hard delete). Field names, not collections, so the
    // registry lint doesn't apply (inlined to hold the 120 KB weight law).
    const gigs = allGigs.filter((g) => g.state.gig?.fields["deleted"] !== true);
    // Tour-book order everywhere: chronological by the gig header date,
    // undated last — never opaque-id order (2026-07-02 review).
    gigs.sort(compareGigs);
    setBoot({ painted: true, gigs, all: allGigs, meta });
    // ADR-011: keep meta.travel_window as fresh as the data on screen.
    void store.putTravelWindow(computeTravelWindow(gigs, Date.now())).catch(() => undefined);
  }, []);

  const refreshAttention = useCallback(async () => {
    try {
      const M = machineryRef.current;
      if (M === null) return;
      setAttentionItems(await M.fetchAttention());
    } catch {
      /* offline / pre-login: keep the last known list */
    }
  }, []);

  useEffect(() => {
    let disposed = false;
    let sse: SseHandle | null = null;
    let tickTimer: ReturnType<typeof setInterval> | null = null;
    let unwireFlush: (() => void) | null = null;
    let unwireUploads: (() => void) | null = null;
    let unsubCount: (() => void) | null = null;
    let unsubConflicts: (() => void) | null = null;

    const run = async () => {
      // Machinery chunk resolves while the shell paints (weight law — the
      // gate weighs the entry; nothing below runs before the paint anyway).
      const M = (machineryRef.current ??= await import("./machinery.ts"));
      const {
        HLCClock, Outbox, Flusher, wireFlushTriggers, Editor, persistentClientId,
        UndoStack, ConflictTracker, fetchBootstrap, fetchChanges, SyncEngine,
        openTickleStream, SseWatchdog, fetchViewerGigs, scrubShareKeyFromUrl,
        shareKeyFromUrl, probeMe, UploadQueue, openUploadStore, wireUploadTriggers,
        registryVersion,
      } = M;
      // --- 1. local-first paint, no network involved ---
      try {
        storeRef.current = await openLocalDb();
      } catch {
        // IndexedDB unavailable (rare; lockdown/private modes): stay usable
        // online, honest-empty offline — never crash the boot path.
        storeRef.current = new MemoryStore();
      }
      if (disposed) return;
      const store = storeRef.current;
      await repaint();

      // --- share-key VIEWER branch: read-only, no outbox/SSE/authed-/changes ---
      const viewerKey = shareKeyFromUrl();
      if (viewerKey !== null) {
        // Keep a viewer's scoped data in memory only — never persist another
        // person's PII-scoped gig into IndexedDB on a possibly-shared phone.
        storeRef.current = new MemoryStore();
        const vStore = storeRef.current;
        scrubShareKeyFromUrl(); // ADR-014: the credential never lingers in the URL
        const loadViewer = async () => {
          try {
            const gigs = await fetchViewerGigs(viewerKey);
            await vStore.commitDelta(gigs, {
              cursor: 0,
              schema_version: registryVersion,
              last_synced_at: Date.now(),
            });
            setOnline(true);
            setMode("live");
          } catch {
            setOnline(navigator.onLine); // bad/revoked key or offline: keep last paint
          }
          if (!disposed) await repaint();
        };
        viewerReloadRef.current = () => void loadViewer();
        await loadViewer();
        tickTimer = setInterval(() => void loadViewer(), VIEWER_POLL_MS);
        return; // viewers skip ALL of the owner/editor machinery below
      }

      if (standalone) void ensurePersistentStorage();

      // --- 2. sync + editing machinery, only after the paint ---
      const clock = new HLCClock(persistentClientId());
      const conflicts = new ConflictTracker(clock.clientId);
      conflictsRef.current = conflicts;
      unsubConflicts = conflicts.onChange(() => setConflictTick((n) => n + 1));

      const engine = new SyncEngine(store, fetchChanges, () => void repaint(), {
        fetchBootstrap, // first sync on imported state (ops predate the log)
        onRemoteOps(entries) {
          // HLC receive rule: local stamps stay causally after everything seen.
          for (const { op } of entries) {
            try {
              clock.observe(op.hlc);
            } catch {
              /* malformed stamp — applyOps already parked the op */
            }
          }
          conflicts.observeRemote(entries);
        },
      });
      engineRef.current = engine;

      const outbox = new Outbox(store);
      unsubCount = outbox.onCountChange(setPendingCount);
      await outbox.init(); // leftover queue from a force-quit → badge is honest at boot

      const flusher = new Flusher({
        outbox,
        store,
        onStatus: setFlushStatus,
        onAcked: (opIds) => conflicts.markAcked(opIds),
        onParked: () => void refreshAttention(),
        checkReplication,
      });
      flusherRef.current = flusher;
      unwireFlush = wireFlushTriggers(flusher);

      const undo = new UndoStack(store);
      undoRef.current = undo;
      editorRef.current = new Editor({
        outbox,
        engine,
        clock,
        actor: "owner", // the P1 auth stub's single principal; real identity in P3
        undo,
        conflicts,
        onAfterEnqueue: () => void flusher.kick(),
      });

      const pull = async () => {
        try {
          const outcome = await engine.pull();
          setEngineAttention(outcome.needsAttention);
          setOnline(true);
          setLoginSurfaces(null); // a successful pull IS proof of a session
          await repaint(); // refresh last-synced even on no-op pulls
        } catch {
          setOnline(navigator.onLine);
          // A failed pull is ambiguous: offline OR no session. /api/me is the
          // discriminator (pre-auth, cheap). Only a confirmed 401 shows Login —
          // an offline device keeps its local-first paint (never a login wall
          // over cached data it can legitimately read).
          if (navigator.onLine) {
            const me = await probeMe();
            if (disposed) return;
            if (me.kind === "unauthenticated") setLoginSurfaces(me.surfaces);
          }
        }
      };

      const watchdog: InstanceType<typeof SseWatchdog> = new SseWatchdog({
        connect(lastEventId) {
          sse?.close();
          sse = openTickleStream(
            {
              onOpen: () => watchdog.onOpen(Date.now()),
              onComment: (_c, buildId) => watchdog.onHeartbeat(Date.now(), buildId),
              onTickle: (_tickle, eventId) => {
                watchdog.onTickle(Date.now(), eventId);
                void pull(); // tickle = wake-up signal ONLY; data via /changes
              },
              onDown: () => watchdog.onStreamDown(Date.now()),
            },
            lastEventId,
          );
        },
        disconnect() {
          sse?.close();
          sse = null;
        },
        poll() {
          void pull();
        },
        onModeChange(m: SyncMode) {
          setMode(m);
        },
        onBuildChange() {
          void requestSwUpdate(); // version skew → SW update flow (ADR-003)
        },
      });

      pullRef.current = () => void pull();

      // Upload queue (P4 files): opens AFTER openLocalDb upgraded the DB, so
      // the uploads store exists. onStored kicks the pull — the synced files
      // row replaces the in-flight queue row near-instantly.
      try {
        const uploadStore = await openUploadStore();
        const queue = new UploadQueue({ store: uploadStore, onStored: () => void pull() });
        await queue.init(); // leftover files from a force-quit resume honestly
        uploadQueueRef.current = queue;
        unwireUploads = wireUploadTriggers(queue);
        setUploadQueueReady(true);
      } catch {
        /* IndexedDB unavailable (private mode) — files UI degrades to browse-only */
      }

      void flusher.kick(); // replay whatever the last session left behind…
      void pull(); // …catch-up delta…
      void refreshAttention();
      watchdog.start(Date.now()); // …then live tickles
      tickTimer = setInterval(() => watchdog.tick(Date.now()), WATCHDOG_TICK_MS);
    };

    void run();

    const onOnline = () => {
      setOnline(true);
      if (viewerReloadRef.current !== null) {
        viewerReloadRef.current(); // viewer session: re-fetch scoped gigs
        return;
      }
      void flusherRef.current?.kick(); // flush the outbox on every connectivity window…
      void engineRef.current?.pull().then(() => repaint()); // …and pull the delta
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      disposed = true;
      sse?.close();
      if (tickTimer !== null) clearInterval(tickTimer);
      flusherRef.current?.dispose(); // cancels any armed backoff-retry timer
      unwireFlush?.();
      unwireUploads?.();
      unsubCount?.();
      unsubConflicts?.();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [repaint, refreshAttention, standalone]);

  // Keyboard model (cockpit): the console is ALWAYS armed. Any printable key
  // outside an editable element focuses the console (the keystroke lands in
  // the freshly focused input — hands never leave the keys); ⌘K focuses and
  // clears; ⌘Z is the global undo (outside editable elements).
  useEffect(() => {
    const editing = (): boolean => {
      const el = document.activeElement;
      if (el === null) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement).isContentEditable;
    };
    const consoleInput = (): HTMLInputElement | null =>
      document.getElementById("cockpit-console-input") as HTMLInputElement | null;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        consoleInput()?.focus();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z") && !editing()) {
        e.preventDefault();
        void editorRef.current?.undoLast().then((done) => {
          if (done) {
            pushJournal(t(locale, "console.undone"), "accent");
            tutorialTick("undo");
          }
        });
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1 || editing()) return;
      consoleInput()?.focus(); // the keystroke itself lands in the input
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [locale, pushJournal, tutorialTick]);

  // Live clock (signage bar), one-minute tick.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const todayFocus = useMemo(() => pickTodayFocus(boot.gigs, nowMs), [boot.gigs, nowMs]);

  // Mirror the current hero to the native shell (Live Activity / Lock Screen
  // widget) — the `homeHero.ts` seam bridge.ts references. No-op in a browser;
  // the payload is ADR-014 name-scrubbed (toTDGlance). Deduped so a stable
  // moment isn't re-posted on every clock tick.
  const lastGlanceSig = useRef("");
  useEffect(() => {
    if (todayFocus === null) return;
    const g = toTDGlance(todayFocus, nowMs, locale);
    const sig = g === null ? "" : JSON.stringify(g);
    if (sig === lastGlanceSig.current) return;
    lastGlanceSig.current = sig;
    if (g !== null) postGlance(g);
  }, [todayFocus, nowMs, locale]);

  // Desktop: nothing "selected" still means the next show is open — the
  // content pane is a briefing, never a void.
  const focusGigId = selected?.gigId ?? (isDesktop ? (todayFocus?.gig.gig_id ?? boot.gigs[0]?.gig_id ?? null) : null);
  const selectedGig = boot.gigs.find((g) => g.gig_id === focusGigId) ?? null;
  const neverSynced = boot.meta === null || boot.meta.last_synced_at === null;
  const attentionCount = attentionItems.length + engineAttention;
  const editor = editorRef.current;
  const conflictsTracker = conflictsRef.current;
  const flusher = flusherRef.current;

  const openGig = useCallback((gigId: string, split: SplitId = "day") => {
    setView("gig");
    setSelected({ gigId, split });
  }, []);

  // The bar's focus line: today's verdict, spoken once (DST-safe, Today.tsx).
  const focusLine = useMemo(() => {
    if (todayFocus === null) return t(locale, "verdict.free_today");
    const v = computeVerdict(nowMs, todayFocus.tz, { day: todayFocus.day }, todayFocus.next);
    if (v === null) return gigDisplayName(todayFocus.gig);
    return v.key === "today.verdict.leave"
      ? t(locale, "today.verdict.leave").replace("{t}", v.leaveText ?? "")
      : t(locale, v.key);
  }, [todayFocus, nowMs, locale]);

  // Honest save state text for the bar (pending → flushing → synced/replicated).
  const saveText = !online
    ? t(locale, "status.offline")
    : pendingCount > 0
      ? flushStatus.flushing
        ? t(locale, "save.sending")
        : t(locale, "save.pending", { n: pendingCount })
      : boot.meta?.last_synced_at != null
        ? `${t(locale, "save.synced_word")} · ${new Date(boot.meta.last_synced_at).toLocaleTimeString(locale === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" })}`
        : t(locale, "save.synced_word");
  const saveTone: "ok" | "busy" | "warn" = !online || attentionCount > 0 ? "warn" : pendingCount > 0 ? "busy" : "ok";

  const setPrefs = useCallback(
    (patch: { theme?: Theme; lang?: Locale; units?: "km" | "mi" }) => {
      if (patch.lang !== undefined) {
        // Load the new locale's lazy catalog BEFORE flipping, so the UI never
        // flashes raw keys (the chunk is tiny / already cached after boot).
        const lang = patch.lang;
        void loadCatalog(lang).then(() => setLocale(lang));
        persistLocale(lang);
      }
      if (patch.theme !== undefined) {
        setTheme(patch.theme);
        try {
          localStorage.setItem("td-theme", patch.theme);
        } catch {
          /* private mode */
        }
      }
      if (patch.units !== undefined) {
        setUnits(patch.units);
        try {
          localStorage.setItem("td-units", patch.units);
        } catch {
          /* private mode */
        }
      }
    },
    [],
  );

  const dismissAttention = useCallback(
    async (id: string) => {
      try {
        const res = await fetch(`/api/attention/${encodeURIComponent(id)}/dismiss`, {
          method: "POST",
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error(String(res.status));
        await refreshAttention();
        pushJournal(t(locale, "rail.attention_dismissed"), "muted");
      } catch {
        /* stays listed — dismiss must be visibly effective or visibly not */
      }
    },
    [refreshAttention, pushJournal, locale],
  );

  /** Dispatch one agent mission — fire-and-track; the fleet is just several
   * of these in flight at once. The server agent works and returns findings
   * + REGISTRY-VALIDATED cards; nothing applies without the user's ↵. */
  const dispatchMission = useCallback(
    (mission: "audit" | "advance", gigId: string) => {
      const gig = boot.gigs.find((g) => g.gig_id === gigId);
      const label = `${mission === "audit" ? t(locale, "mission.audit") : t(locale, "mission.advance")} — ${gig !== null && gig !== undefined ? gigDisplayName(gig) : gigId}`;
      const id = `m${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setMissions((ms) => [{ id, label, status: "run" as const, steps: [], cards: [], summary: "" }, ...ms].slice(0, 5));
      pushJournal(t(locale, "mission.dispatched", { label }), "accent");
      void (async () => {
        try {
          const { runMission } = await import("../cockpit/fleet.ts");
          const r = await runMission(mission, gigId, locale);
          setMissions((ms) => ms.map((m) => (m.id === id ? { ...m, status: "done" as const, steps: r.steps, cards: r.cards, summary: r.summary } : m)));
          pushJournal(t(locale, "mission.done", { label }), "ok");
        } catch {
          setMissions((ms) => ms.map((m) => (m.id === id ? { ...m, status: "failed" as const } : m)));
          pushJournal(t(locale, "mission.failed", { label }), "warn");
        }
      })();
    },
    [boot.gigs, locale, pushJournal],
  );

  /** «+ ajouter une ligne» — pre-guessed, never a blank form: after the last
   * schedule row, kind run, through the REAL op path (undoable). */
  const addPreGuessedRow = useCallback(async () => {
    const gig = selectedGig;
    if (gig === null || editorRef.current === null) return;
    const SCHEDULE = registry.schedule.key;
    const ST = registry.schedule.fields.start_time.key;
    const rows = Object.values(gig.state.collections[SCHEDULE] ?? {});
    let last = "";
    for (const r of rows) {
      const v = (r as { fields: Record<string, unknown> }).fields[ST];
      if (typeof v === "string" && v > last) last = v;
    }
    const guess = (() => {
      const m = /^([0-2]\d):([0-5]\d)$/.exec(last);
      if (m === null) return "12:00";
      const tt = (Number(m[1]) * 60 + Number(m[2]) + 60) % 1440;
      return `${String(Math.floor(tt / 60)).padStart(2, "0")}:${String(tt % 60).padStart(2, "0")}`;
    })();
    await editorRef.current.createItem(gig.gig_id, SCHEDULE, {
      [registry.schedule.fields.title.key]: t(locale, "fiche.new_row"),
      [ST]: guess,
    });
    pushJournal(t(locale, "fiche.row_added"), "ok");
  }, [selectedGig, locale, pushJournal]);

  // Confirmed 401 (never mere offline — see pull()): the login IS the screen.
  if (loginSurfaces !== null && capabilities.canWrite) {
    return (
      <Suspense fallback={<p className="empty-note">{t(locale, "app.loading")}</p>}>
        <Login
          locale={locale}
          surfaces={loginSurfaces}
          onSignedIn={() => {
            setLoginSurfaces(null);
            pullRef.current?.(); // resume: catch-up delta…
            void flusherRef.current?.kick(); // …and push anything saved while signed out
          }}
          onRetryProbe={() => pullRef.current?.()}
        />
      </Suspense>
    );
  }

  const showRail = view === "gig" && (isDesktop || selected === null);
  const showMain = view !== "gig" || isDesktop || selected !== null;

  const mainContent = (() => {
    if (!boot.painted) return <p className="empty-note">{t(locale, "app.loading")}</p>;
    if (view === "attention" && capabilities.canWrite && editor !== null && flusher !== null) {
      return (
        <Suspense fallback={null}>
          <Attention
            locale={locale}
            editor={editor}
            flusher={flusher}
            onBack={() => setView("gig")}
            onCountChange={() => void refreshAttention()}
          />
        </Suspense>
      );
    }
    if (view === "vault") {
      return (
        <Suspense fallback={<p className="empty-note">{t(locale, "app.loading")}</p>}>
          <FilesVault
            locale={locale}
            gigs={boot.gigs}
            onBack={() => setView("gig")}
            onOpenGig={(gigId) => openGig(gigId, "gig")}
            focusFileId={vaultFocusFile}
            editor={capabilities.canWrite ? (editor ?? undefined) : undefined}
            focusGigId={focusGigId}
          />
        </Suspense>
      );
    }
    if (view === "rolo" && capabilities.canWrite) {
      return (
        <Suspense fallback={<p className="empty-note">{t(locale, "app.loading")}</p>}>
          <Rolodex
            locale={locale}
            gigs={boot.gigs}
            editor={editor ?? undefined}
            onBack={() => setView("gig")}
            onOpenGig={(gigId) => openGig(gigId, "people")}
          />
        </Suspense>
      );
    }
    if (view === "settings" && capabilities.canWrite) {
      return (
        <Suspense fallback={<p className="empty-note">{t(locale, "app.loading")}</p>}>
          <Settings
            locale={locale}
            gigs={boot.gigs}
            prefs={{ theme, units }}
            setPrefs={setPrefs}
            onAnswer={(text, kind) => pushJournal(text, kind === "ok" ? "ok" : "warn")}
            pushJournal={pushJournal}
          />
        </Suspense>
      );
    }
    if (selectedGig !== null && (selected !== null || isDesktop)) {
      return (
        <Suspense fallback={<p className="empty-note">{t(locale, "app.loading")}</p>}>
        <GigDetail
          key={`${selectedGig.gig_id}:${selected?.split ?? "day"}`}
          locale={locale}
          gig={selectedGig}
          editor={editor ?? undefined}
          conflicts={conflictsTracker ?? undefined}
          canUndo={undoRef.current?.canUndo === true}
          onBack={() => setSelected(null)}
          initialSplit={selected?.split ?? "day"}
          uploadQueue={uploadQueueReady ? (uploadQueueRef.current ?? undefined) : undefined}
          briefIndex={boot.gigs.findIndex((g) => g.gig_id === selectedGig.gig_id) + 1}
          briefTotal={boot.gigs.length}
          onFicheOpened={() => tutorialTick("fiche")}
          onSeam={(key) => pushJournal(t(locale, key), "warn")}
          allGigs={boot.gigs}
        />
        </Suspense>
      );
    }
    if (boot.gigs.length === 0 && neverSynced && !online) {
      return (
        <div className="honest-empty">
          <h2>{t(locale, "app.offline_empty_title")}</h2>
          <p>{t(locale, "app.offline_empty_body")}</p>
        </div>
      );
    }
    return null; // mobile, nothing selected: the rail IS the screen
  })();

  return (
    <CapabilitiesProvider value={capabilities}>
      <div className="cockpit">
        <SignageBar
          locale={locale}
          isDesktop={isDesktop}
          focusLine={focusLine}
          view={view === "attention" ? "gig" : view}
          canWrite={capabilities.canWrite}
          onView={(v) => {
            setView(v);
            if (v === "gig") setSelected(null);
            tutorialTick("view");
          }}
          onStartTutorial={() => {
            setTourStep(0);
            setView("gig");
          }}
          saveText={attentionCount > 0 ? `${saveText} · !${attentionCount}` : saveText}
          saveTone={saveTone}
          onSaveNow={() => {
            if (attentionCount > 0) setView("attention");
            else {
              void flusherRef.current?.kick();
              pushJournal(t(locale, "save.forced_journal"), "ok");
            }
          }}
          nowMs={nowMs}
        />

        {!capabilities.canWrite && <div className="viewer-banner">{t(locale, "viewer.read_only")}</div>}
        {!standalone && capabilities.canWrite && !isDesktop && !installDismissed && (
          <div className="install-banner">
            <span>{t(locale, "app.install_banner")}</span>
            <button
              type="button"
              aria-label={t(locale, "rail.attention_dismiss")}
              onClick={() => {
                setInstallDismissed(true);
                try { localStorage.setItem("td-install-dismissed", "1"); } catch { /* private mode */ }
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="cockpit-body">
          {/* Mobile home = the GLANCE HERO (GlanceHero.tsx): context line + the
              one thing that matters this minute (leave/next/quiet) + the
              concierge sentence; one tap = the focus gig. The boarding-pass
              CREDENTIAL state is the TravelCard fast-lane overlay; rest-of-day is
              the Rail below. Desktop keeps the bar focus line + master-detail. */}
          {!isDesktop && showRail && boot.painted && todayFocus !== null && (
            <GlanceHero locale={locale} focus={todayFocus} nowMs={nowMs} onOpen={(id) => openGig(id)} />
          )}
          {showRail && boot.painted && (
            <Rail
              locale={locale}
              gigs={boot.gigs}
              all={boot.all}
              editor={editor ?? undefined}
              openGigId={view === "gig" ? (selectedGig?.gig_id ?? null) : null}
              canWrite={capabilities.canWrite}
              nowMs={nowMs}
              attention={attentionItems}
              journal={journal}
              onOpen={(gigId) => openGig(gigId)}
              onNewGig={() => setNewGig(null)}
              onDismissAttention={(id) => void dismissAttention(id)}
              onPrefillConsole={(q) => prefillRef.current?.(q)}
              missions={missions}
              onLoadMissionCards={(m) => {
                externalCardsRef.current?.(m.cards, m.label);
                pushJournal(t(locale, "mission.cards_loaded", { label: m.label }), "muted");
              }}
            />
          )}
          {showMain && <section className="ck-main">{mainContent}</section>}
        </div>

        <Console
          locale={locale}
          view={view === "attention" ? "gig" : view}
          gigs={boot.gigs}
          focusGig={selectedGig ?? todayFocus?.gig ?? null}
          editor={editor}
          canWrite={capabilities.canWrite}
          isMobile={!isDesktop}
          uploadQueue={uploadQueueReady ? (uploadQueueRef.current ?? undefined) : undefined}
          onJump={openGig}
          onView={(v) => {
            setView(v);
            if (v === "gig") setSelected(null);
          }}
          onOpenPerson={() => setView("rolo")}
          onStartTutorial={() => {
            setTourStep(0);
            setView("gig");
          }}
          onNewGig={(prefill) => setNewGig(prefill ?? null)}
          onAddRow={() => void addPreGuessedRow()}
          onForceSave={() => {
            void flusherRef.current?.kick();
            pushJournal(t(locale, "save.forced_journal"), "ok");
          }}
          onOpenFile={(fileItemId) => {
            setVaultFocusFile(fileItemId);
            setView("vault");
          }}
          pushJournal={pushJournal}
          tutorialTick={tutorialTick}
          tutorial={{ step: tourStep, total: TUTORIAL_STEPS }}
          onTutorialNext={() => setTourStep((s) => (s + 1 >= TUTORIAL_STEPS ? -1 : s + 1))}
          onTutorialQuit={() => setTourStep(-1)}
          setPrefs={setPrefs}
          registerPrefill={(fn) => {
            prefillRef.current = fn;
          }}
          onDispatchMission={dispatchMission}
          registerExternalCards={(fn) => {
            externalCardsRef.current = fn;
          }}
        />

        {newGig !== false && capabilities.canWrite && editor !== null && (
          <Suspense fallback={null}>
            <NewGigFiche
              locale={locale}
              gigs={boot.gigs}
              editor={editor}
              prefill={newGig ?? undefined}
              nowMs={nowMs}
              onClose={() => setNewGig(false)}
              onCreated={(gigId, line) => {
                setNewGig(false);
                pushJournal(t(locale, "newgig.created", { line }), "ok");
                openGig(gigId);
              }}
            />
          </Suspense>
        )}
      </div>
    </CapabilitiesProvider>
  );
}
